import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import roomsService from "../services/roomsService";
import AssignGuardianModal from "../components/AssignGuardianModal";
import styles from "../assets/propertiesRoomsSearch.module.css";

const SortIcon = ({ direction, isActive }) => (
  <svg
    width="10"
    height="16"
    viewBox="0 0 10 16"
    style={{ marginLeft: 4, verticalAlign: "middle" }}
  >
    <g fill={isActive ? "#333" : "#bbb"}>
      {direction === "asc" ? (
        <polygon points="5,3 8,7 2,7" />
      ) : direction === "desc" ? (
        <polygon points="5,13 2,9 8,9" />
      ) : (
        <>
          <polygon points="5,3 8,7 2,7" />
          <polygon points="5,13 2,9 8,9" />
        </>
      )}
    </g>
  </svg>
);

const PropertiesRoomsSearch = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true); // Initial page load
  const [tableLoading, setTableLoading] = useState(false); // Table-only loading (filters, search, sort)
  const [rooms, setRooms] = useState([]);
  const [filteredRooms, setFilteredRooms] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [recordsTotal, setRecordsTotal] = useState(0); // Total records from backend
  const [tableControls, setTableControls] = useState({
    entriesPerPage: 30,
    searchTerm: "",
    showColumnVisibility: false,
  });

  // Column visibility state - matching Laravel DataTables config exactly
  // Laravel: keys, property_id, property_am are visible: false by default
  const [visibleColumns, setVisibleColumns] = useState({
    name: true,
    status: true,
    keys: false, // Laravel: visible: false
    couple: true,
    guardian: true,
    start: true,
    end: true,
    price: true,
    propertyName: true,
    propertyId: false, // Laravel: visible: false, title: 'ID'
    propertyAM: false, // Laravel: visible: false, title: 'AM'
    postcode: true,
    noticeDate: true,
    client: true,
    actions: true,
  });

  // Room overview counts - will be loaded from API
  const [roomCounts, setRoomCounts] = useState({
    all: 0,
    vacant: 0,
    occupied: 0,
    noticeGiven: 0,
    other: 0,
  });

  // Filter state
  const [filters, setFilters] = useState({
    statusId: 999,
    typeId: 999,
  });

  // State for sorting
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");

  // State for Assign Guardian Modal
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  // Reload data when currentPage changes
  useEffect(() => {
    if (!loading) {
      loadRoomsData();
    }
  }, [currentPage]);

  // Handle sorting - triggers server-side data reload
  const handleSort = async (field) => {
    let newSortField = sortField;
    let newSortDirection = sortDirection;

    if (sortField === field) {
      newSortDirection = sortDirection === "asc" ? "desc" : "asc";
    } else {
      newSortField = field;
      newSortDirection = "asc";
    }

    // Update state
    setSortField(newSortField);
    setSortDirection(newSortDirection);
    setCurrentPage(1);

    // Reload data with new sorting (pass directly to avoid state timing issues)
    await loadRoomsData(null, null, { sortField: newSortField, sortDirection: newSortDirection });
  };

  const loadInitialData = async () => {
    try {
      setLoading(true);

      // Load room counts
      await loadRoomCounts();

      // Load initial rooms data (pass isInitialLoad = true)
      await loadRoomsData(null, null, null, true);
    } catch (error) {
      console.error("Error loading initial data:", error);
      toast.error("Failed to load initial data");
    } finally {
      setLoading(false);
    }
  };

  const loadRoomCounts = async () => {
    try {
      console.log("🔍 [FRONTEND] Loading room counts...");
      const counts = await roomsService.getRoomCounts();
      console.log("✅ [FRONTEND] Room counts received:", counts);
      setRoomCounts(counts);
    } catch (error) {
      console.error("Error loading room counts:", error);
      toast.error("Failed to load room counts");
      // Set default counts if API fails
      setRoomCounts({
        all: 0,
        vacant: 0,
        occupied: 0,
        noticeGiven: 0,
        other: 0,
      });
    }
  };

  const loadRoomsData = async (overrideFilters = null, overrideSearchTerm = null, overrideSorting = null, isInitialLoad = false) => {
    try {
      // Use full page loading only for initial load, table loading for subsequent updates
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setTableLoading(true);
      }

      // Use override filters if provided, otherwise use state filters
      const activeFilters = overrideFilters || filters;

      // Use override search term if provided, otherwise use state search term
      const activeSearchTerm = overrideSearchTerm !== null ? overrideSearchTerm : tableControls.searchTerm;

      // Use override sorting if provided, otherwise use state sorting
      const activeSorting = overrideSorting || { sortField, sortDirection };

      const params = {
        statusId: activeFilters.statusId === 999 ? null : activeFilters.statusId,
        typeId: activeFilters.typeId === 999 ? null : activeFilters.typeId,
        page: currentPage,
        perPage: tableControls.entriesPerPage,
        search: activeSearchTerm,
        sortField: activeSorting.sortField,
        sortDirection: activeSorting.sortDirection,
      };

      const response = await roomsService.getRoomsSearch(params);

      // Transform API data to match our component structure
      const transformedRooms = response.data.map((room) => {
        // Debug: Log guardian data structure
        if (room.id === response.data[0]?.id) {
          console.log("🔍 [FRONTEND] First room guardian data:", {
            guardian: room.guardian,
            hasGuardian: !!room.guardian,
            guardianType: typeof room.guardian,
            guardianKeys: room.guardian ? Object.keys(room.guardian) : null,
            fullName: room.guardian?.fullName,
          });
        }
        
        return {
          id: room.id,
          name: room.name,
          status: room.status_text,
          statusId: room.status_id,
          statusColor: room.status_color,
          key_number: room.key_number || 1,
          isCouple: room.is_couple,
          guardian: room.guardian, // Keep guardian object as-is from backend
        startDate: room.start,
        endDate: room.end,
        price: room.price,
        property: {
          id: room.property.id,
          name: room.property.name,
          property_id: room.property.property_id,
          postcode: room.property.postcode,
          client: {
            id: room.property.client.id,
            name: room.property.client.name,
            manager: room.property.client.manager,
          },
        },
          noticeDate: room.date_notice
            ? (() => {
                const date = new Date(room.date_notice);
                // Check if date is valid before formatting to prevent NaN
                if (isNaN(date.getTime())) return null;
                const day = String(date.getDate()).padStart(2, "0");
                const month = String(date.getMonth() + 1).padStart(2, "0");
                const year = date.getFullYear();
                const hours = String(date.getHours()).padStart(2, "0");
                const minutes = String(date.getMinutes()).padStart(2, "0");
                return `${day}/${month}/${year} ${hours}:${minutes}`;
              })()
            : null,
          typeId: room.type_id,
          typeText: room.type_text,
          typeColor: room.type_color,
        };
      });

      setRooms(transformedRooms);
      setFilteredRooms(transformedRooms);

      // Update pagination info from backend response
      // Use recordsFiltered for filtered count, recordsTotal for total count
      const filteredCount = response.recordsFiltered || response.recordsTotal || 0;
      setRecordsTotal(filteredCount);
      setTotalPages(
        Math.ceil(filteredCount / tableControls.entriesPerPage)
      );
    } catch (error) {
      console.error("Error loading rooms data:", error);
      toast.error("Failed to load rooms data");
      setRooms([]);
      setFilteredRooms([]);
      setRecordsTotal(0);
    } finally {
      setLoading(false);
      setTableLoading(false);
    }
  };

  const handleFilterChange = async (statusId, typeId) => {
    const newFilters = { statusId, typeId };
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filter changes
    await loadRoomsData(newFilters); // Reload data with new filters immediately
  };

  const handleTableControlChange = async (e) => {
    const { name, value } = e.target;

    // Convert entriesPerPage to number if it's a numeric field
    const processedValue =
      name === "entriesPerPage" ? parseInt(value, 10) : value;

    setTableControls((prev) => ({
      ...prev,
      [name]: processedValue,
    }));

    // Handle search filtering
    if (name === "searchTerm") {
      setCurrentPage(1);
      // Pass the new search term directly to avoid async state update issue
      await loadRoomsData(null, processedValue); // Reload data with search term
    }

    // Handle entries per page change
    if (name === "entriesPerPage") {
      setCurrentPage(1);
      await loadRoomsData(); // Reload data with new page size
    }
  };

  const handleExportCSV = () => {
    try {
      const dataToExport = filteredRooms;

      // Build headers based on visible columns
      const headers = [];
      if (visibleColumns.name) headers.push("Name");
      if (visibleColumns.status) headers.push("Status");
      if (visibleColumns.couple) headers.push("Couple?");
      if (visibleColumns.guardian) headers.push("Guardian");
      if (visibleColumns.start) headers.push("Start rent");
      if (visibleColumns.end) headers.push("End rent");
      if (visibleColumns.price) headers.push("Price");
      if (visibleColumns.property) headers.push("Property");
      if (visibleColumns.postcode) headers.push("Postcode");
      if (visibleColumns.noticeDate) headers.push("Notice Date");
      if (visibleColumns.client) headers.push("Client");

      const csvContent = [
        headers.join(","),
        ...dataToExport.map((room) => {
          const row = [];
          if (visibleColumns.name) row.push(`"${room.name}"`);
          if (visibleColumns.status) row.push(`"${room.status}"`);
          if (visibleColumns.keys) row.push(`"${room.key_number || "-"}"`);
          if (visibleColumns.couple) row.push(room.isCouple ? "Yes" : "No");
          if (visibleColumns.guardian) row.push(`"${room.guardian || "-"}"`);
          if (visibleColumns.start) row.push(`"${room.startDate || "-"}"`);
          if (visibleColumns.end) row.push(`"${room.endDate || "-"}"`);
          if (visibleColumns.price) row.push(`"${room.price}"`);
          if (visibleColumns.propertyName) row.push(`"${room.property.name}"`);
          if (visibleColumns.propertyId) row.push(`"${room.property.property_id || "-"}"`);
          if (visibleColumns.propertyAM) {
            const manager = room.property.client?.manager;
            row.push(`"${manager ? `${manager.first_name} ${manager.last_name}` : "-"}"`);
          }
          if (visibleColumns.postcode) row.push(`"${room.property.postcode}"`);
          if (visibleColumns.noticeDate)
            row.push(`"${room.noticeDate || "-"}"`);
          if (visibleColumns.client) row.push(`"${room.property.client.name}"`);
          return row.join(",");
        }),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `rooms_search_${new Date().toISOString().split("T")[0]}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success("CSV file downloaded successfully!");
    } catch (error) {
      console.error("Error exporting CSV:", error);
      toast.error("Failed to export CSV");
    }
  };

  const handleExportPDF = async () => {
    try {
      console.log("📄 [FRONTEND] Starting PDF export for Rooms Search...");

      // Create PDF content using jsPDF (copying from ExpenseAccount.jsx)
      const { jsPDF } = await import("jspdf");
      const doc = new jsPDF();

      // Add title
      doc.setFontSize(16);
      doc.text("Rooms Search Report", 20, 20);
      doc.setFontSize(10);
      doc.text(
        `Generated on: ${new Date().toLocaleDateString("en-GB")}`,
        20,
        30
      );
      doc.text(`Total Records: ${filteredRooms.length}`, 20, 40);

      // Add table headers
      const headers = [];
      if (visibleColumns.name) headers.push("Name");
      if (visibleColumns.status) headers.push("Status");
      if (visibleColumns.keys) headers.push("Keys");
      if (visibleColumns.couple) headers.push("Couple?");
      if (visibleColumns.guardian) headers.push("Guardian");
      if (visibleColumns.start) headers.push("Start rent");
      if (visibleColumns.end) headers.push("End rent");
      if (visibleColumns.price) headers.push("Price");
      if (visibleColumns.propertyName) headers.push("Property");
      if (visibleColumns.propertyId) headers.push("ID");
      if (visibleColumns.propertyAM) headers.push("AM");
      if (visibleColumns.postcode) headers.push("Postcode");
      if (visibleColumns.noticeDate) headers.push("Notice Date");
      if (visibleColumns.client) headers.push("Client");

      let yPosition = 60;
      const startX = 20;
      const colWidth = 25;

      // Draw headers
      headers.forEach((header, index) => {
        doc.text(header, startX + index * colWidth, yPosition);
      });

      yPosition += 10;

      // Draw data rows
      filteredRooms.forEach((room, index) => {
        if (yPosition > 280) {
          doc.addPage();
          yPosition = 20;
        }

        const rowData = [];
        if (visibleColumns.name)
          rowData.push(String(room.name || "").substring(0, 20));
        if (visibleColumns.status)
          rowData.push(String(room.status || "").substring(0, 20));
        if (visibleColumns.keys)
          rowData.push(String(room.key_number || "-").substring(0, 20));
        if (visibleColumns.couple)
          rowData.push(String(room.isCouple ? "Yes" : "No").substring(0, 20));
        if (visibleColumns.guardian)
          rowData.push(String(room.guardian || "-").substring(0, 20));
        if (visibleColumns.start)
          rowData.push(String(room.startDate || "-").substring(0, 20));
        if (visibleColumns.end)
          rowData.push(String(room.endDate || "-").substring(0, 20));
        if (visibleColumns.price)
          rowData.push(String(room.price || "").substring(0, 20));
        if (visibleColumns.propertyName)
          rowData.push(String(room.property?.name || "").substring(0, 20));
        if (visibleColumns.propertyId)
          rowData.push(String(room.property?.property_id || "-").substring(0, 20));
        if (visibleColumns.propertyAM) {
          const manager = room.property?.client?.manager;
          rowData.push(String(manager ? `${manager.first_name} ${manager.last_name}` : "-").substring(0, 20));
        }
        if (visibleColumns.postcode)
          rowData.push(String(room.property?.postcode || "").substring(0, 20));
        if (visibleColumns.noticeDate)
          rowData.push(String(room.noticeDate || "-").substring(0, 20));
        if (visibleColumns.client)
          rowData.push(
            String(room.property?.client?.name || "").substring(0, 20)
          );

        rowData.forEach((cell, cellIndex) => {
          const safeCell = String(cell || "");
          doc.text(
            safeCell.substring(0, 20),
            startX + cellIndex * colWidth,
            yPosition
          );
        });

        yPosition += 7;
      });

      // Save PDF
      doc.save(`rooms_search_${new Date().toISOString().split("T")[0]}.pdf`);

      console.log("PDF exported successfully!");
      toast.success("PDF exported successfully!");
    } catch (error) {
      console.error("Export PDF error:", error);
      toast.error(`Failed to export PDF: ${error.message}`);
    }
  };

  const handleColumnVisibility = () => {
    setTableControls((prev) => ({
      ...prev,
      showColumnVisibility: !prev.showColumnVisibility,
    }));
  };

  const toggleColumnVisibility = (column) => {
    setVisibleColumns((prev) => ({
      ...prev,
      [column]: !prev[column],
    }));
  };

  const handleAssignGuardian = (roomId, propertyId, roomName, propertyName) => {
    // Open modal for assigning guardian (matching Laravel behavior)
    setSelectedRoom({
      roomId,
      propertyId,
      roomName,
      propertyName,
    });
    setAssignModalOpen(true);
  };

  const handleAssignSuccess = () => {
    // Reload rooms data after successful assignment
    loadRoomsData();
    toast.success("Guardian assigned successfully!");
  };

  // Calculate pagination (server-side pagination - backend already returns correct page)
  const entriesPerPage = parseInt(tableControls.entriesPerPage, 10) || 30;

  // Ensure current page is valid
  const validCurrentPage = Math.min(Math.max(1, currentPage), totalPages || 1);
  if (validCurrentPage !== currentPage && totalPages > 0) {
    setCurrentPage(validCurrentPage);
  }

  // Server-side pagination: backend already returns the correct page, so use rooms directly
  const currentRooms = rooms; // Backend already paginated, no need to slice

  // Calculate pagination display values
  const startIndex = (validCurrentPage - 1) * entriesPerPage;
  const endIndex = Math.min(startIndex + currentRooms.length, recordsTotal);

  // Debug logging
  console.log("Pagination debug:", {
    entriesPerPage,
    totalPages,
    currentPage: validCurrentPage,
    startIndex: startIndex + 1, // Display is 1-based
    endIndex,
    recordsTotal,
    currentRoomsLength: currentRooms.length,
  });

  const getStatusColor = (status) => {
    switch (status) {
      case "vacant":
        return "#5bc0de";
      case "occupied":
        return "#dc3545";
      case "notice_given":
        return "#007bff";
      case "other":
        return "#fd7e14";
      default:
        return "#777";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "vacant":
        return "VACANT";
      case "occupied":
        return "OCCUPIED";
      case "notice_given":
        return "NOTICE GIVEN";
      case "other":
        return "OTHER";
      default:
        return "UNKNOWN";
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading rooms search...</div>
      </div>
    );
  }



  return (
    <div className={styles.container}>
      {/* Breadcrumb */}
      <div className={styles.breadcrumb}>
        <span onClick={() => navigate("/dashboard")}>Dashboard</span> /{" "}
        <span onClick={() => navigate("/properties")}>Properties</span> /{" "}
        <span className={styles.breadcrumbCurrent}>Rooms search</span>
      </div>

      <div className={styles.content}>
        {/* Rooms Overview */}
        <div className={styles.overviewSection}>
          <div className={styles.overviewTitle}>
            <h3>Rooms overview</h3>
          </div>
          <div className={styles.overviewBoxes}>
            <div
              className={styles.overviewBox}
              style={{ backgroundColor: "#5bc0de" }}
              onClick={() => handleFilterChange(999, 999)}
            >
              <div className={styles.overviewValue}>{roomCounts.all}</div>
              <div className={styles.overviewTitle}>All</div>
            </div>
            <div
              className={styles.overviewBox}
              style={{ backgroundColor: "#28a745" }}
              onClick={() => handleFilterChange(0, 1)}
            >
              <div className={styles.overviewValue}>{roomCounts.vacant}</div>
              <div className={styles.overviewTitle}>Vacant</div>
            </div>
            <div
              className={styles.overviewBox}
              style={{ backgroundColor: "#dc3545" }}
              onClick={() => handleFilterChange(1, 1)}
            >
              <div className={styles.overviewValue}>{roomCounts.occupied}</div>
              <div className={styles.overviewTitle}>Occupied</div>
            </div>
            <div
              className={styles.overviewBox}
              style={{ backgroundColor: "#007bff" }}
              onClick={() => handleFilterChange(2, 1)}
            >
              <div className={styles.overviewValue}>
                {roomCounts.noticeGiven}
              </div>
              <div className={styles.overviewTitle}>Notice given</div>
            </div>
            <div
              className={styles.overviewBox}
              style={{ backgroundColor: "#fd7e14" }}
              onClick={() => handleFilterChange(999, 2)}
            >
              <div className={styles.overviewValue}>{roomCounts.other}</div>
              <div className={styles.overviewTitle}>Other</div>
            </div>
          </div>
        </div>

        {/* Main Panel */}
        <div className={styles.panel}>
          <div className={styles.panelHeader}>
            <h3>Rooms of active properties</h3>
          </div>
          <div className={styles.panelBody}>
            {/* Table Controls */}
            <div className={styles.tableControls}>
              <div className={styles.tableControlsLeft}>
                <span>Show</span>
                <select
                  name="entriesPerPage"
                  value={tableControls.entriesPerPage}
                  onChange={handleTableControlChange}
                  className={styles.entriesSelect}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={30}>30</option>
                  <option value={50}>50</option>
                  <option value={75}>75</option>
                  <option value={100}>100</option>
                </select>
                <span>entries</span>
              </div>

              <div className={styles.tableControlsRight}>
                <div className={styles.searchControl}>
                  <label>Search:</label>
                  <input
                    type="text"
                    name="searchTerm"
                    value={tableControls.searchTerm}
                    onChange={handleTableControlChange}
                    className={styles.searchInput}
                    placeholder="Search..."
                  />
                </div>

                <div className={styles.actionButtons}>
                  <button
                    type="button"
                    className={styles.actionBtn}
                    onClick={handleExportCSV}
                  >
                    Export as CSV
                  </button>
                  <button
                    type="button"
                    className={styles.actionBtn}
                    onClick={handleExportPDF}
                  >
                    Export as PDF
                  </button>
                  <div className={styles.columnVisibilityContainer}>
                    <button
                      type="button"
                      className={styles.actionBtn}
                      onClick={handleColumnVisibility}
                    >
                      Column visibility
                    </button>

                    {/* Column Visibility Dropdown */}
                    {tableControls.showColumnVisibility && (
                      <div className={styles.columnVisibilityDropdown}>
                        <div className={styles.columnVisibilityHeader}>
                          <h4>Column Visibility</h4>
                          <button
                            type="button"
                            className={styles.closeBtn}
                            onClick={() =>
                              setTableControls((prev) => ({
                                ...prev,
                                showColumnVisibility: false,
                              }))
                            }
                          >
                            ×
                          </button>
                        </div>
                        <div className={styles.columnVisibilityContent}>
                          {Object.entries(visibleColumns).map(
                            ([column, isVisible]) => (
                              <label
                                key={column}
                                className={styles.columnVisibilityItem}
                              >
                                <input
                                  type="checkbox"
                                  checked={isVisible}
                                  onChange={() =>
                                    toggleColumnVisibility(column)
                                  }
                                />
                                <span className={styles.columnLabel}>
                                  {column === "propertyName"
                                    ? "Property"
                                    : column === "propertyId"
                                    ? "ID"
                                    : column === "propertyAM"
                                    ? "AM"
                                    : column === "noticeDate"
                                    ? "Notice Date"
                                    : column === "isCouple"
                                    ? "Couple?"
                                    : column.charAt(0).toUpperCase() +
                                      column.slice(1)}
                                </span>
                              </label>
                            )
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Results Table */}
            {!loading && !tableLoading && recordsTotal === 0 ? (
              <div className={styles.noData}>No rooms!</div>
            ) : currentRooms.length > 0 || tableLoading ? (
              <div className={styles.tableWrapper} style={{ position: "relative" }}>
                {/* Table loading overlay */}
                {tableLoading && (
                  <div style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(255, 255, 255, 0.7)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 10
                  }}>
                    <div style={{ fontSize: "14px", color: "#666" }}>Loading...</div>
                  </div>
                )}
                <table className={styles.dataTable}>
                  <thead>
                    <tr>
                      {visibleColumns.name && (
                        <th
                          className="sortable"
                          onClick={() => handleSort("name")}
                          style={{ cursor: "pointer" }}
                        >
                          Name
                          <SortIcon
                            direction={
                              sortField === "name" ? sortDirection : null
                            }
                            isActive={sortField === "name"}
                          />
                        </th>
                      )}
                      {visibleColumns.status && (
                        <th
                          className="sortable"
                          onClick={() => handleSort("status")}
                          style={{ cursor: "pointer" }}
                        >
                          Status
                          <SortIcon
                            direction={
                              sortField === "status" ? sortDirection : null
                            }
                            isActive={sortField === "status"}
                          />
                        </th>
                      )}
                      {visibleColumns.keys && (
                        <th
                          className="sortable"
                          onClick={() => handleSort("key_number")}
                          style={{ cursor: "pointer", textAlign: "left" }}
                        >
                          Keys
                          <SortIcon
                            direction={
                              sortField === "key_number" ? sortDirection : null
                            }
                            isActive={sortField === "key_number"}
                          />
                        </th>
                      )}
                      {visibleColumns.couple && (
                        <th
                          className="sortable"
                          onClick={() => handleSort("isCouple")}
                          style={{ cursor: "pointer" }}
                        >
                          Couple?
                          <SortIcon
                            direction={
                              sortField === "isCouple" ? sortDirection : null
                            }
                            isActive={sortField === "isCouple"}
                          />
                        </th>
                      )}
                      {visibleColumns.guardian && (
                        <th
                          className="sortable"
                          onClick={() => handleSort("guardian")}
                          style={{ cursor: "pointer" }}
                        >
                          Guardian
                          <SortIcon
                            direction={
                              sortField === "guardian" ? sortDirection : null
                            }
                            isActive={sortField === "guardian"}
                          />
                        </th>
                      )}
                      {visibleColumns.start && (
                        <th
                          className="sortable"
                          onClick={() => handleSort("start")}
                          style={{ cursor: "pointer" }}
                        >
                          Start rent
                          <SortIcon
                            direction={
                              sortField === "start" ? sortDirection : null
                            }
                            isActive={sortField === "start"}
                          />
                        </th>
                      )}
                      {visibleColumns.end && (
                        <th
                          className="sortable"
                          onClick={() => handleSort("end")}
                          style={{ cursor: "pointer" }}
                        >
                          End rent
                          <SortIcon
                            direction={
                              sortField === "end" ? sortDirection : null
                            }
                            isActive={sortField === "end"}
                          />
                        </th>
                      )}
                      {visibleColumns.price && (
                        <th
                          className="sortable"
                          onClick={() => handleSort("price")}
                          style={{ cursor: "pointer" }}
                        >
                          Price
                          <SortIcon
                            direction={
                              sortField === "price" ? sortDirection : null
                            }
                            isActive={sortField === "price"}
                          />
                        </th>
                      )}
                      {visibleColumns.propertyName && (
                        <th
                          className="sortable"
                          onClick={() => handleSort("propertyName")}
                          style={{ cursor: "pointer" }}
                        >
                          Property
                          <SortIcon
                            direction={
                              sortField === "propertyName" ? sortDirection : null
                            }
                            isActive={sortField === "propertyName"}
                          />
                        </th>
                      )}
                      {visibleColumns.propertyId && (
                        <th
                          className="sortable"
                          onClick={() => handleSort("propertyId")}
                          style={{ cursor: "pointer" }}
                        >
                          ID
                          <SortIcon
                            direction={
                              sortField === "propertyId" ? sortDirection : null
                            }
                            isActive={sortField === "propertyId"}
                          />
                        </th>
                      )}
                      {visibleColumns.propertyAM && (
                        <th
                          className="sortable"
                          onClick={() => handleSort("propertyAM")}
                          style={{ cursor: "pointer" }}
                        >
                          AM
                          <SortIcon
                            direction={
                              sortField === "propertyAM" ? sortDirection : null
                            }
                            isActive={sortField === "propertyAM"}
                          />
                        </th>
                      )}
                      {visibleColumns.postcode && (
                        <th
                          className="sortable"
                          onClick={() => handleSort("postcode")}
                          style={{ cursor: "pointer" }}
                        >
                          Postcode
                          <SortIcon
                            direction={
                              sortField === "postcode" ? sortDirection : null
                            }
                            isActive={sortField === "postcode"}
                          />
                        </th>
                      )}
                      {visibleColumns.noticeDate && (
                        <th
                          className="sortable"
                          onClick={() => handleSort("noticeDate")}
                          style={{ cursor: "pointer" }}
                        >
                          Notice Date
                          <SortIcon
                            direction={
                              sortField === "noticeDate" ? sortDirection : null
                            }
                            isActive={sortField === "noticeDate"}
                          />
                        </th>
                      )}
                      {visibleColumns.client && (
                        <th
                          className="sortable"
                          onClick={() => handleSort("client")}
                          style={{ cursor: "pointer" }}
                        >
                          Client
                          <SortIcon
                            direction={
                              sortField === "client" ? sortDirection : null
                            }
                            isActive={sortField === "client"}
                          />
                        </th>
                      )}
                      {visibleColumns.actions && <th>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {currentRooms.map((room, index) => (
                      <tr
                        key={room.id}
                        className={index % 2 === 1 ? styles.evenRow : ""}
                      >
                        {visibleColumns.name && (
                          <td className={styles.roomName}>{room.name}</td>
                        )}
                        {visibleColumns.status && (
                          <td>
                            <span
                              className={styles.statusBadge}
                              style={{ backgroundColor: room.statusColor }}
                            >
                              {room.status}
                            </span>
                          </td>
                        )}
                        {visibleColumns.keys && (
                          <td style={{ textAlign: "left" }}>
                            {room.key_number || "-"}
                          </td>
                        )}
                        {visibleColumns.couple && (
                          <td>{room.isCouple ? "Yes" : "No"}</td>
                        )}
                        {visibleColumns.guardian && (
                          <td>
                            {room.guardian && room.guardian.id ? (
                              <span
                                onClick={() =>
                                  navigate(`/guardians/${room.guardian.id}`)
                                }
                                className={styles.guardianLink}
                                style={{ cursor: "pointer" }}
                                title={room.guardian.fullName ? `Click to view ${room.guardian.fullName}` : "Click to view guardian"}
                              >
                                {room.guardian.fullName || "Unknown Guardian"}
                              </span>
                            ) : (
                              "-"
                            )}
                          </td>
                        )}
                        {visibleColumns.start && (
                          <td>{room.startDate || "-"}</td>
                        )}
                        {visibleColumns.end && <td>{room.endDate || "-"}</td>}
                        {visibleColumns.price && <td>{room.price}</td>}
                        {visibleColumns.propertyName && (
                          <td>
                            <span
                              onClick={() =>
                                navigate(`/properties/${room.property.id}`)
                              }
                              className={styles.propertyLink}
                              style={{ cursor: "pointer" }}
                            >
                              {room.property.name}
                            </span>
                          </td>
                        )}
                        {visibleColumns.propertyId && (
                          <td>
                            <span
                              onClick={() =>
                                navigate(`/properties/${room.property.id}`)
                              }
                              className={styles.propertyLink}
                              style={{ cursor: "pointer" }}
                            >
                              {room.property.property_id || "-"}
                            </span>
                          </td>
                        )}
                        {visibleColumns.propertyAM && (
                          <td>
                            {room.property.client?.manager
                              ? `${room.property.client.manager.first_name} ${room.property.client.manager.last_name}`
                              : "-"}
                          </td>
                        )}
                        {visibleColumns.postcode && (
                          <td>{room.property.postcode}</td>
                        )}
                        {visibleColumns.noticeDate && (
                          <td>{room.noticeDate || "-"}</td>
                        )}
                        {visibleColumns.client && (
                          <td>
                            <span
                              onClick={() =>
                                navigate(`/clients/${room.property.client.id}`)
                              }
                              className={styles.clientLink}
                              style={{ cursor: "pointer" }}
                            >
                              {room.property.client.name}
                            </span>
                          </td>
                        )}
                        {visibleColumns.actions && (
                          <td>
                            {/* Debug: Log first room's statusId and typeId */}
                            {room.id === rooms[0]?.id && console.log("🔍 Debug first room:", {
                              id: room.id,
                              statusId: room.statusId,
                              typeId: room.typeId,
                              status: room.status,
                              statusIdType: typeof room.statusId,
                              typeIdType: typeof room.typeId,
                              condition: room.statusId === 0 && room.typeId === 1
                            })}
                            {/* Show Assign button for vacant guardian rooms (statusId 0 = VACANT, typeId 1 = Guardian) */}
                            {(room.statusId === 0 || room.statusId === "0") && (room.typeId === 1 || room.typeId === "1") && (
                              <button
                                type="button"
                                className={styles.assignBtn}
                                onClick={() =>
                                  handleAssignGuardian(
                                    room.id,
                                    room.property.id,
                                    room.name,
                                    room.property.name
                                  )
                                }
                              >
                                Assign
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : !loading ? (
              <div className={styles.noData}>No rooms on this page!</div>
            ) : null}

            {/* Pagination */}
            {recordsTotal > 0 && (
              <div className={styles.pagination}>
                <div className={styles.paginationInfo}>
                  Showing {startIndex + 1} to {endIndex} of{" "}
                  {recordsTotal.toLocaleString()} entries
                </div>
                <div className={styles.paginationControls}>
                  <button
                    type="button"
                    className={styles.paginationBtn}
                    disabled={validCurrentPage === 1}
                    onClick={() => setCurrentPage(validCurrentPage - 1)}
                  >
                    Previous
                  </button>

                  {/* First page */}
                  <button
                    type="button"
                    className={`${styles.paginationBtn} ${
                      validCurrentPage === 1 ? styles.activePage : ""
                    }`}
                    onClick={() => setCurrentPage(1)}
                  >
                    1
                  </button>

                  {/* Show ellipsis if there are many pages before current */}
                  {validCurrentPage > 4 && (
                    <span className={styles.paginationEllipsis}>...</span>
                  )}

                  {/* Pages around current page */}
                  {(() => {
                    const pages = [];
                    const startPage = Math.max(2, validCurrentPage - 2);
                    const endPage = Math.min(
                      totalPages - 1,
                      validCurrentPage + 2
                    );

                    for (
                      let pageNum = startPage;
                      pageNum <= endPage;
                      pageNum++
                    ) {
                      if (pageNum > 1 && pageNum < totalPages) {
                        pages.push(
                          <button
                            key={pageNum}
                            type="button"
                            className={`${styles.paginationBtn} ${
                              validCurrentPage === pageNum
                                ? styles.activePage
                                : ""
                            }`}
                            onClick={() => setCurrentPage(pageNum)}
                          >
                            {pageNum}
                          </button>
                        );
                      }
                    }
                    return pages;
                  })()}

                  {/* Show ellipsis if there are many pages after current */}
                  {validCurrentPage < totalPages - 3 && (
                    <span className={styles.paginationEllipsis}>...</span>
                  )}

                  {/* Last page */}
                  {totalPages > 1 && (
                    <button
                      type="button"
                      className={`${styles.paginationBtn} ${
                        validCurrentPage === totalPages ? styles.activePage : ""
                      }`}
                      onClick={() => setCurrentPage(totalPages)}
                    >
                      {totalPages}
                    </button>
                  )}

                  <button
                    type="button"
                    className={styles.paginationBtn}
                    disabled={validCurrentPage === totalPages}
                    onClick={() => setCurrentPage(validCurrentPage + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Assign Guardian Modal */}
      <AssignGuardianModal
        isOpen={assignModalOpen}
        onClose={() => {
          setAssignModalOpen(false);
          setSelectedRoom(null);
        }}
        propertyId={selectedRoom?.propertyId}
        roomId={selectedRoom?.roomId}
        propertyName={selectedRoom?.propertyName}
        roomName={selectedRoom?.roomName}
        onAssignSuccess={handleAssignSuccess}
      />
    </div>
  );
};

export default PropertiesRoomsSearch;
